import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Eula, ForgotPassword, User, UserAction } from '@core/models';
import { AppState } from '@core/store/reducers';
import { Store } from '@ngrx/store';
import moment from 'moment';
import { firstValueFrom, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { ConnectionResponse } from '../models/user/connection-response';
import { IUser } from '../models/user/i-user';
import { acceptEula, loadUserSuccess, setConnection } from '../store/actions/user.actions';
import { UsersState } from '../store/reducers/users.reducer';
import { ConfigService } from './config.service';

@Injectable()
export class UsersService {
  apiPath: string;
  baseUserPath: string;
  state: Observable<UsersState>;

  user: User;
  tkn: string;
  publicPaths: string[];
  userPromise: Promise<User>;
  firstTime = true;
  publicPath: string;

  constructor(private configService: ConfigService, private httpClient: HttpClient, private store: Store<AppState>) {
    this.publicPath = this.configService.appConfig.apic.baseUrl + this.configService.appConfig.apic.publicPath + this.configService.appConfig.api.baseUrl;
    this.apiPath = this.configService.appConfig.apic.baseUrl + this.configService.appConfig.apic.apiPath + this.configService.appConfig.api.baseUrl;
    this.baseUserPath = this.apiPath + '/user';
  }

  async pubtk(): Promise<ConnectionResponse> {
    return await firstValueFrom(this.httpClient
      .post<ConnectionResponse>(`${this.publicPath}/pubtk`, '', {
        observe: 'response'
      }))
      .then((response) => {
        if (!this.tkn) {
          const tkn = response.headers.get('Set-Tkn');
          const publicPaths = response.body.publicPaths;
          this.store.dispatch(setConnection({ usersState: { tkn: tkn, publicPaths: publicPaths } }));
        }
        return response.body;
      })
      .catch(() => <ConnectionResponse>{ connected: false });
  }

  async ptk(): Promise<ConnectionResponse> {
    return await firstValueFrom(this.httpClient
      .post<ConnectionResponse>(`${this.apiPath}/ptk`, '', {
        observe: 'response'
      }))
      .then((response) => {
        if (!this.tkn) {
          const tkn = response.headers.get('Set-Tkn');
          const publicPaths = response.body.publicPaths;
          this.store.dispatch(setConnection({ usersState: { tkn: tkn, publicPaths: publicPaths } }));
        }
        return response.body;
      })
      .catch(() => <ConnectionResponse>{ connected: false });
  }

  loadUser(): Promise<User> {
    return firstValueFrom(this.httpClient
      .post<IUser>(`${this.apiPath}/user/getUser`, null))
      .then((u) => {
        const user = User.userFactory(u);
        this.store.dispatch(loadUserSuccess({ user: user }));
        return user;
      });
  }

  getUsers(): Observable<User[]> {
    return this.httpClient.get<User[]>(`${this.baseUserPath}/list`);
  }

  getOrgUsers(orgId: number): Observable<User[]> {
    return this.httpClient.get<User[]>(`${this.baseUserPath}/getOrgUsers/${orgId}`).pipe(
      map((response) => {
        const users: User[] = [];

        response.forEach((userData) => {
          users.push(User.userFactory(userData));
        });
        return users;
      })
    );
  }

  createOrUpdate(userAction: UserAction): Observable<UserAction> {
    if (userAction.user.userId) {
      return this.httpClient.put<User>(`${this.baseUserPath}/edit`, userAction.user).pipe(map(response => {
        if (response) {
          return userAction;
        }
      }));
    } else {
      return this.httpClient.post<User>(`${this.baseUserPath}/create`, userAction.user).pipe(map(response => {
        if (response) {
          return userAction
        }
      }));
    }
  }

  delete(userId: number): Observable<any> {
    return this.httpClient.delete(`${this.baseUserPath}/delete/${userId}`);
  }

  resendActivation(forgotPasswordData: ForgotPassword): Promise<any> {
    return firstValueFrom(this.httpClient
      .post(`${this.baseUserPath}/resendActivation`, forgotPasswordData, { observe: 'response' }));
  }

  getEula(): Promise<string> {
    return firstValueFrom(this.httpClient
      .get<Eula>(`${this.apiPath}/eula/text`))
      .then((response) => response.eulaText);
  }

  acceptEula(userData: User): Promise<User> {
    const now = moment().format('YYYY-MM-dd');
    return firstValueFrom(this.httpClient
      .put(`${this.baseUserPath}/eula`, ''))
      .then(() => {
        const userInfo = User.userFactory(userData);
        userInfo.eulaAcceptedTime = now;
        const userAction = {
          user: userInfo
        } as UserAction;

        this.store.dispatch(acceptEula({ userAction: userAction }));
        return userInfo;
      });
  }

  updatePreference(user: User): Promise<User> {
    return firstValueFrom(this.httpClient.put<User>(`${this.baseUserPath}/edit`, user));
  }

  checkUser(userLoginName: string): Promise<any> {
    return firstValueFrom(this.httpClient.post<User>(`${this.apiPath}/checkUser`, { "userLoginName": userLoginName }));
  }

}